import {
  Container,
  Flex,
  Paper,
  Text,
  Badge,
  Tabs,
  Anchor,
  Title,
  Box,
  Button,
  Divider,
  SimpleGrid,
  Stack,
} from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconWorld,
  IconExternalLink,
  IconArrowLeft,
  IconClipboardList,
  IconEye,
} from "@tabler/icons-react";
import MatchedProfileTab from "@/pages/profile/MatchedPrfofielTab";
import StatusBadge from "@/pages/profile-verification/StatusBadge";
import DocumentsTab from "@/components/shared/AnonymousCompanyProfile/DocumentsTab";
import ImagesTabContent from "@/components/profile/ImagesTabContent";
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TAB_LABEL_FZ,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navyProfileNavTabBorderRadius,
} from "@/shared/styles/navTabsLayout";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";

/** 16px row icons: gutter on `base` so title / GTID align with location–email text; collapses from `sm`. */
const MATCHED_HEADER_DETAIL_ICON_COL_PX = 24;

export type QuestionnaireCta = {
  type: "start" | "view";
  onClick: () => void;
};

interface MatchedProfileViewProps {
  company?: any;
  containerSize?: "fluid";
  isOwnProfile?: boolean;
  /** Collaboration workspace: questionnaire action inside the header card */
  questionnaireCta?: QuestionnaireCta | null;
  /**
   * Collaboration workspace overview only: mobile stacks company block then full-width Partner Readiness CTA
   * (reference card UI — pale panel, navy border/shadow, bold title, vertical contact rows).
   */
  mobileCollabWorkspaceCard?: boolean;
  onDownloadVerificationPdf?: () => void | Promise<void>;
  verificationPdfDownloading?: boolean;
}

export default function MatchedProfileView({
  company: propCompany,
  containerSize = "fluid",
  isOwnProfile = false,
  questionnaireCta = null,
  mobileCollabWorkspaceCard = false,
  onDownloadVerificationPdf,
  verificationPdfDownloading,
}: MatchedProfileViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, hasProfile } = useUserContext();

  const [company, setCompany] = useState<any>(propCompany || null);
  const [collaboration, setCollaboration] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const {
    isMobile: isProfileNavMobile,
    tabsListStyles: profileNavTabsListStyles,
    tabsRootStyles: profileNavTabsRootStyles,
  } = useNavTabsStackLayout(2);

  // Check if we're on the collaboration profile route
  const isCollaborationProfile =
    location.pathname.includes("/collaborations/") && location.pathname.includes("/profile");

  useEffect(() => {
    // Only fetch if we don't have company data from props and we're on collaboration profile route
    if (hasProfile === null) return;
    if (!propCompany && isCollaborationProfile && id) {
      const fetchCompanyData = async () => {
        try {
          const collabRecord = await trpc.collaboration.getById.query({ matchId: id });
          setCollaboration(collabRecord);

          const myCompanyId = userDetails?.company?.id;
          const otherCompanyId =
            collabRecord.requesterCompanyId === myCompanyId
              ? collabRecord.receiverCompanyId
              : collabRecord.requesterCompanyId;

          const companyData = await trpc.company.getById.query({ companyId: otherCompanyId });
          setCompany(companyData);
        } catch (err) {
          console.error("Error fetching company:", err);
          handleTrpcError(err);
        }
      };

      fetchCompanyData();
    }
  }, [id, propCompany, isCollaborationProfile, userDetails?.company?.id, hasProfile]);

  // Fetch logo URL from S3 when company changes
  useEffect(() => {
    if (company?.logo?.id) {
      getFileUrl(company.logo.id).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  // Safety check - if no company data, show error
  if (!company) {
    return (
      <Container size={containerSize} py="xl">
        <Text>No company data available</Text>
      </Container>
    );
  }

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      {/* Back Button - Only show on collaboration profile route */}
      {isCollaborationProfile && (
        <Box py="sm" px={{ base: "md", sm: "xl" }}>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate(`/collaborations/${id}`)}
          >
            Back to Workspace
          </Button>
        </Box>
      )}

      <Container size={containerSize} pb="xs" px={{ base: "sm", sm: "md" }}>
        {/* Company Header - Shows REAL data */}
        <Paper
          p={{ base: mobileCollabWorkspaceCard ? "lg" : "md", sm: "xl" }}
          radius="lg"
          mb="xl"
          style={{
            border: "1px solid #04305c",
            backgroundColor: "#ebf0ff",
            ...(mobileCollabWorkspaceCard
              ? { boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)", overflow: "hidden" }
              : {}),
          }}
        >
          <Flex
            gap="md"
            align="flex-start"
            justify="space-between"
            direction={{ base: mobileCollabWorkspaceCard ? "column" : "row", sm: "row" }}
            wrap="nowrap"
            w="100%"
            miw={0}
          >
            <Flex
              gap="md"
              align="flex-start"
              miw={0}
              w={{ base: mobileCollabWorkspaceCard ? "100%" : undefined, sm: undefined }}
              style={{ flex: 1, minWidth: 0 }}
            >
            <Flex
              w={{ base: 48, sm: 80 }}
              h={{ base: 48, sm: 80 }}
              style={{
                borderRadius: 8,
                border: "1px solid #04305c",
                backgroundColor: "#F9FAFB",
                flexShrink: 0,
                overflow: "hidden",
              }}
              align="center"
              justify="center"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <IconBuilding size={32} color="#6B7280" />
              )}
            </Flex>

            <Flex direction="column" style={{ flex: 1, minWidth: 0 }} gap={6} miw={0}>
              {/* Row 1: Company Name + Verified Badge */}
              <Flex align="flex-start" gap={6} wrap="nowrap" miw={0}>
                <Box
                  miw={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                  w={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                  style={{ flexShrink: 0 }}
                />
                <Flex align="center" gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
                  <Title
                    order={2}
                    fw={{ base: mobileCollabWorkspaceCard ? 700 : 400, sm: 400 }}
                    fz={{ base: mobileCollabWorkspaceCard ? 19 : 18, sm: 22 }}
                    c={mobileCollabWorkspaceCard ? "#04305c" : undefined}
                    style={{ wordBreak: "break-word" }}
                  >
                    {company.legalName || "Company Name"}
                  </Title>
                  {company.status === "VERIFIED" && (
                    <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                  )}
                </Flex>
              </Flex>

              {/* Row 2: GTID Badge */}
              {company.gtid && (
                <Flex align="flex-start" gap={6} wrap="nowrap" miw={0}>
                  <Box
                    miw={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                    w={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                    style={{ flexShrink: 0 }}
                  />
                  <Badge
                    variant="light"
                    color="#EEF2F7"
                    size="xl"
                    radius="sm"
                    style={{
                      width: "fit-content",
                      backgroundColor: "#EEF2F7",
                      color: "#374151",
                    }}
                  >
                    {company.gtid}
                  </Badge>
                </Flex>
              )}

              {/* Row 3: Location, Phone, Email */}
              <Flex
                gap="md"
                wrap="wrap"
                align="flex-start"
                direction={
                  mobileCollabWorkspaceCard
                    ? { base: "column", sm: "row" }
                    : { base: "column", xs: "row" }
                }
              >
                <Flex align="flex-start" gap={6} wrap="nowrap" miw={0} style={{ maxWidth: "100%" }}>
                  <Box
                    miw={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                    w={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                    style={{ flexShrink: 0 }}
                  />
                  {headquartersFlagEmoji ? (
                    <Text
                      component="span"
                      fz={18}
                      lh={1}
                      pt={2}
                      role="img"
                      aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                      style={{ flexShrink: 0 }}
                    >
                      {headquartersFlagEmoji}
                    </Text>
                  ) : null}
                  <Text size="sm" c="slate.7" style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                    {company.headquartersCity && company.headquartersCountry
                      ? `${company.headquartersCity}, ${company.headquartersCountry}`
                      : company.headquartersCountry || "N/A"}
                  </Text>
                </Flex>
                <Flex align="flex-start" gap={6} wrap="nowrap" miw={0} style={{ maxWidth: "100%" }}>
                  <Flex
                    miw={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                    w={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: "auto" }}
                    justify="center"
                    align="flex-start"
                    pt={2}
                    style={{ flexShrink: 0 }}
                  >
                    <IconPhone size={16} color="#6B7280" />
                  </Flex>
                  <Text size="sm" c="slate.7" style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                    {company.phoneCountryCode && company.phoneNumber
                      ? `${company.phoneCountryCode} ${company.phoneNumber}`
                      : "N/A"}
                  </Text>
                </Flex>
                <Flex align="flex-start" gap={6} wrap="nowrap" miw={0} w="100%" style={{ maxWidth: "100%" }}>
                  <Flex
                    miw={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                    w={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: "auto" }}
                    justify="center"
                    align="flex-start"
                    pt={2}
                    style={{ flexShrink: 0 }}
                  >
                    <IconMail size={16} color="#6B7280" />
                  </Flex>
                  <Text size="sm" c="slate.7" style={{ flex: 1, minWidth: 0, wordBreak: "break-all" }}>
                    {company.email || "N/A"}
                  </Text>
                </Flex>
              </Flex>

              {/* Row 4: Website */}
              <Flex
                gap="md"
                wrap="wrap"
                align="flex-start"
                direction={
                  mobileCollabWorkspaceCard
                    ? { base: "column", sm: "row" }
                    : { base: "column", xs: "row" }
                }
              >
                {company.website && (
                  <Flex align="flex-start" gap={6} wrap="nowrap" miw={0} style={{ maxWidth: "100%" }}>
                    <Flex
                      miw={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: 0 }}
                      w={{ base: MATCHED_HEADER_DETAIL_ICON_COL_PX, sm: "auto" }}
                      justify="center"
                      align="flex-start"
                      pt={2}
                      style={{ flexShrink: 0 }}
                    >
                      <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                    </Flex>
                    <Anchor
                      href={company.website}
                      target="_blank"
                      td="none"
                      style={{ flex: 1, minWidth: 0 }}
                      styles={{
                        root: {
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        },
                      }}
                    >
                      <Flex align="center" gap={6} wrap="wrap">
                        <Text size="sm" c="primary.6">
                          Website
                        </Text>
                        <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
                      </Flex>
                    </Anchor>
                  </Flex>
                )}
              </Flex>
            </Flex>
            </Flex>

            {(questionnaireCta ||
              (isOwnProfile && ["UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED"].includes(company.status))) && (
              <Flex
                direction="column"
                align={{ base: mobileCollabWorkspaceCard ? "stretch" : "flex-end", sm: "flex-end" }}
                gap="sm"
                w={{ base: mobileCollabWorkspaceCard ? "100%" : "auto", sm: "auto" }}
                miw={0}
                style={{ flexShrink: mobileCollabWorkspaceCard ? undefined : 0 }}
              >
                {questionnaireCta && (
                  <Button
                    variant={questionnaireCta.type === "start" ? "filled" : "default"}
                    size={mobileCollabWorkspaceCard ? "md" : "sm"}
                    w={{ base: mobileCollabWorkspaceCard ? "100%" : "auto", sm: "auto" }}
                    maw="100%"
                    radius="md"
                    styles={{
                      root:
                        questionnaireCta.type === "start"
                          ? {
                              backgroundColor: "#ffc300",
                              color: "#04305c",
                              border: "1px solid #ffc300",
                              "&:hover": {
                                backgroundColor: "#e6b000",
                                borderColor: "#e6b000",
                              },
                            }
                          : {
                              backgroundColor: "#ffffff",
                              color: "#04305c",
                              border: "1px solid #04305c",
                              "&:hover": {
                                backgroundColor: "#ebf0ff",
                                borderColor: "#04305c",
                              },
                            },
                    }}
                    leftSection={
                      questionnaireCta.type === "start" ? (
                        <IconClipboardList size={18} color="#04305c" />
                      ) : (
                        <IconEye size={18} color="#04305c" />
                      )
                    }
                    onClick={questionnaireCta.onClick}
                  >
                    {questionnaireCta.type === "start"
                      ? "Open Partner Readiness"
                      : "View Partner Readiness"}
                  </Button>
                )}
                {isOwnProfile && ["UNDER_REVIEW", "VERIFIED", "SYSTEM_VERIFIED"].includes(company.status) && (
                  <StatusBadge status={company.status} />
                )}
              </Flex>
            )}
          </Flex>
        </Paper>

        {/* Tabs Section */}
        <Tabs value={activeTab} onChange={setActiveTab} styles={profileNavTabsRootStyles}>
          <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
            <Tabs.List styles={profileNavTabsListStyles}>
            <Tabs.Tab
              value="profile"
              px={{ base: "md", sm: "lg" }}
              py="xs"
              fz={NAV_TAB_LABEL_FZ}
              style={{
                ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                backgroundColor: activeTab === "profile" ? "#04305c" : "#ebf0ff",
                color: activeTab === "profile" ? "white" : "var(--mantine-color-slate-6)",
                borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "first"),
                border: activeTab === "profile" ? "1px solid #04305c" : "1px solid #E5E7EB",
                borderBottom: "none",
                fontWeight: activeTab === "profile" ? 500 : 400,
                cursor: "pointer",
                minHeight: 44,
              }}
              rightSection={
                <Box
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              }
            >
              Company Profile
            </Tabs.Tab>
            <Tabs.Tab
              value="images"
              px={{ base: "md", sm: "lg" }}
              py="xs"
              fz={NAV_TAB_LABEL_FZ}
              style={{
                ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                backgroundColor: activeTab === "images" ? "#04305c" : "#ebf0ff",
                color: activeTab === "images" ? "white" : "var(--mantine-color-slate-6)",
                borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "last"),
                border: activeTab === "images" ? "1px solid #04305c" : "1px solid #E5E7EB",
                borderBottom: "none",
                fontWeight: activeTab === "images" ? 500 : 400,
                cursor: "pointer",
                minHeight: 44,
              }}
              rightSection={
                <Box
                  w={28}
                  h={28}
                  miw={28}
                  mih={28}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              }
            >
              Images and Documents
            </Tabs.Tab>
            </Tabs.List>
          </Box>

          <Tabs.Panel value="profile" pt="xl">
            <MatchedProfileTab
              company={company}
              collaboration={collaboration}
              onDownloadVerificationPdf={onDownloadVerificationPdf}
              verificationPdfDownloading={verificationPdfDownloading}
            />
          </Tabs.Panel>

          <Tabs.Panel value="images" pt="xl">
            {activeTab === "images" && (
              <ImagesTabContent
                company={company}
                activeTab={activeTab}
                refreshCompany={async () => {}}
                isViewMode={true}
              />
            )}
            <Divider my="xl" size={2} color="#04305c" styles={{ root: { boxShadow: "none" } }} />
            <DocumentsTab company={company} isMatchedView={true} />
          </Tabs.Panel>
        </Tabs>

        {/* About Verified Listings */}
        <Paper
          p={0}
          radius="lg"
          mt="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconBuilding size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              About Verified Listings
            </Text>
          </Flex>
          <Box p={{ base: "lg", sm: "xl" }}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={{ base: "lg", sm: "xl" }} verticalSpacing="md">
              <Stack gap="md">
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Full company details are visible after mutual interest is confirmed
                  </Text>
                </Flex>
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Verification badge confirms company authenticity
                  </Text>
                </Flex>
              </Stack>
              <Stack gap="md">
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Contact information becomes accessible to matched companies
                  </Text>
                </Flex>
                <Flex align="flex-start" gap="sm">
                  <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                  <Text size="sm" fw={700} lh={1.55} c="#04305c">
                    Both parties can initiate collaboration discussions
                  </Text>
                </Flex>
              </Stack>
            </SimpleGrid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
